import React, { useState, useEffect } from "react";

import {
  IonCard,
  IonItem,
  IonLabel,
  IonCardHeader,
  IonCardContent,
  IonIcon,
  IonButton,
  IonRippleEffect,
  IonText,
  IonCol,
  IonGrid,
  IonRow,
  IonNote,
  IonSkeletonText,
  useIonViewWillLeave,
} from "@ionic/react";
import { useParams, Link } from "react-router-dom";
import { gql } from "apollo-boost";
import { useLazyQuery } from "@apollo/react-hooks";

import {
  pinOutline,
  qrCodeOutline,
  arrowRedoOutline,
  documentTextOutline,
  calendarOutline,
  timeOutline,
  cashOutline,
} from "ionicons/icons";

import "./style.css";
import moment from "../../Moment";
import NumberFormat from "../../components/NumberFormat";
import Page from "../../components/Page/Page";
import PageError from "../Error";
import Status from "../../components/Ui/Status";
import Organization from "../../components/Ui/Organization";

import { Ticket } from "../../interfaces/ticket";

const GET_TICKET = gql`
  query getTicket($id: String!) {
    getTicket(id: $id) {
      price
      status
      date
      hour
      minutes
      created_at
      code
      observation
      car {
        number
        organization {
          name
          image
        }
      }
      arrivalLocation {
        name
      }
      departureLocation {
        name
      }
    }
  }
`;

const PassageDetail = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState<Ticket>({
    id: 0,
    date: "",
    hour: 0,
    minutes: 0,
    status: 1,
    arrivalLocation: { name: "" },
    departureLocation: { name: "" },
    code: "",
    observation: "",
    car: {
      board: "",
      number: "",
      organization: {
        name: "",
      },
    },
    price: "",
    created_at: "",
  });
  const [getTicket, { loading, data, error, stopPolling }] = useLazyQuery(
    GET_TICKET,
    {
      variables: {
        id,
      },
      pollInterval: 500,
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    getTicket();
  }, []);

  useEffect(() => {
    if (data && data.getTicket) {
      setTicket((prevTicket) => ({
        ...prevTicket,
        ...data.getTicket,
      }));
    }
  }, [data]);

  useIonViewWillLeave(() => {
    if (stopPolling) stopPolling();
  }, [stopPolling]);

  if (error) {
    return <PageError name="Passagem" error="graphql" />;
  }

  const {
    date,
    hour,
    minutes,
    status,
    arrivalLocation,
    departureLocation,
    code,
    observation,
    car,
    price,
    created_at,
  } = ticket;

  return (
    <Page back="/page/passages" name="Detalhe da passagem">
      <div className="passage-detail-header">
        <Organization loading={loading} organization={car.organization} />
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              {loading ? (
                <IonSkeletonText
                  animated
                  style={{ width: "70%", height: 15 }}
                />
              ) : (
                <IonNote>
                  Realizado às {moment(created_at).format("HH:mm")}
                  {" - "}
                  {moment(created_at).format("DD/MM/YYYY")}
                </IonNote>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
        {loading ? (
          <IonGrid>
            <IonRow>
              <IonCol size="12">
                <IonSkeletonText
                  animated
                  style={{ width: "100%", height: 50 }}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        ) : (
          <Status type={status} />
        )}
      </div>
      <div className="passage-detail">
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              <IonCard className="no-margin-start-end">
                <IonItem>
                  <IonIcon
                    className="icon"
                    slot="start"
                    icon={arrowRedoOutline}
                  />
                  <IonLabel>Saída</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <div>{departureLocation.name}</div>
                  )}
                </IonItem>
                <IonItem>
                  <IonIcon className="icon" slot="start" icon={pinOutline} />
                  <IonLabel>Destino</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <div>{arrivalLocation.name}</div>
                  )}
                </IonItem>
              </IonCard>
              <IonCard className="no-margin-start-end">
                <IonItem lines="inset">
                  <IonIcon
                    className="icon"
                    slot="start"
                    icon={calendarOutline}
                  />
                  <IonLabel>Data</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <span>{moment(date).format("DD/MM/YYYY")}</span>
                  )}
                </IonItem>
                <IonItem lines="inset">
                  <IonIcon className="icon" slot="start" icon={timeOutline} />
                  <IonLabel>Horário</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <span>
                      {hour}:{minutes}
                    </span>
                  )}
                </IonItem>
                <IonItem lines="inset">
                  <IonIcon className="icon" icon={cashOutline} />
                  <IonLabel>Valor</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <span>
                      <NumberFormat value={price} />
                    </span>
                  )}
                </IonItem>
                <IonItem lines="none">
                  <IonIcon
                    className="icon"
                    slot="start"
                    icon={documentTextOutline}
                  />
                  <IonLabel>Observação</IonLabel>
                </IonItem>
                <IonItem lines="inset">
                  <IonIcon className="icon" slot="start" />
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: "100%", height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <IonText
                      className={
                        observation === ""
                          ? "no-observation"
                          : "with-observation"
                      }
                    >
                      {observation || "Sem observação"}
                    </IonText>
                  )}
                </IonItem>
                <IonItem lines="none">
                  <IonIcon className="icon" slot="start" icon={qrCodeOutline} />
                  <IonLabel>Código</IonLabel>
                  {loading ? (
                    <IonSkeletonText
                      animated
                      style={{ width: 100, height: 20 }}
                      slot="end"
                    />
                  ) : (
                    <div>{code}</div>
                  )}
                </IonItem>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <div className="passage-detail-footer">
        <IonCard className="custom-card">
          <IonCardHeader>
            <h3 className="title">Teve algum problema ?</h3>
            {/* <IonCardSubtitle>Pedir Ajuda</IonCardSubtitle> */}
          </IonCardHeader>
          <IonCardContent>
            <Link to={`/page/help/${id}`}>
              <IonButton className="btn-help ion-activatable ripple-parent">
                <IonText color="primary">Pedir Ajuda</IonText>
                <IonRippleEffect />
              </IonButton>
            </Link>
          </IonCardContent>
        </IonCard>
      </div>
    </Page>
  );
};

export default PassageDetail;
